/**
 * Single source of truth for whether this deployment should be indexed by
 * search engines.
 *
 * Only the production deployment (naumu.ai) is crawlable. Staging
 * (stg.naumu.ai) and local development are kept out of the index so that
 * deploying `develop` to staging never creates a duplicate of prod's marketing
 * pages competing for the same SEO keywords.
 *
 * Gated on `NEXT_PUBLIC_DEPLOYMENT` — the explicit, canonical environment
 * signal already used for analytics/env-prefix gating — rather than parsing
 * `NEXT_PUBLIC_URL`, so a custom domain or a missing env var can't accidentally
 * make a non-prod deployment indexable. The default (env unset) is the safe
 * NON-indexable path.
 */
export function isIndexableDeployment(): boolean {
	return process.env.NEXT_PUBLIC_DEPLOYMENT === 'production';
}

/**
 * `robots` value for Next.js `Metadata`. Emits `<meta name="robots" ...>` and
 * is honored by Next when generating the page. On non-prod deployments this is
 * `noindex, nofollow`; on prod it's `undefined` so pages keep their default
 * (indexable) behavior and per-page overrides (e.g. /email-preferences) win.
 */
export function robotsMetadata(): { index: false; follow: false } | undefined {
	return isIndexableDeployment() ? undefined : { index: false, follow: false };
}
